<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { useMemberAssessmentStore } from '@/stores/memberAssessment';
import { trans } from 'laravel-vue-i18n';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const optionsStore = useOptionsStore();
const { scaledOptions } = storeToRefs(optionsStore);

const props = defineProps({
    assessment: {
        type: Object,
        default: () => ({
            //
        })
    },

    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const toast = useToast();
const { saveQuestionsResponses, loadQuestionsAndResponses } = useMemberAssessmentStore();

const questionsResponses = ref([]);

const completionPercentage = computed(() => {
    if (!questionsResponses.value.length) {
        return 0;
    }
    let answered = 0;
    questionsResponses.value.forEach(item => {
        const response = item.responses?.[0] || {};
        if (response.scaled_response_id != null || (response.short_answer != null && response.short_answer !== '')) {
            answered++;
        }
    });
    return Math.round((answered / questionsResponses.value.length) * 100);
});

const allowEdit = computed(() => {
    return props.assessment.status.name !== 'Submitted';
});

onMounted(async () => {
    const result = await loadQuestionsAndResponses(props.assessment);
    if (result && result.data.questions_responses) {
        questionsResponses.value = result.data.questions_responses;
    }
});

const save = async () => {
    try {
        await saveQuestionsResponses({
            questions_responses: questionsResponses.value,
            completion_percentage: completionPercentage.value,
            hash: props.assessment.hash
        });
        
        toast.success(trans('actions.update.success', { type: trans('label.member_assessment') }));
        emit('close-modal');
    } catch (error) {
        toast.error(trans('actions.update.error', { type: trans('label.member_assessment') }));
    }
};

</script>

<template>
<ModalWrapper style="height:1000px;">
    <template #header>{{ $t('label.mode_what', { 'mode' : 'Display', 'what' : $t('label.member_assessment') }) }}</template>
    <template #subheader>{{ $t('label.display_member_assessment_questions_responses') }}</template>
    <form @submit.prevent="save">
        <!-- Progress Bar -->
        <div class="sticky -top-4 z-10 bg-white">
            <div class="w-full max-w-lg bg-grey-200 p-6 rounded-lg mb-6">
                <div class="font-semibold mb-2">{{ $t('label.completion_percentage') }}</div>
                <div class="w-full bg-grey rounded-full h-4">
                    <div
                        class="bg-primary h-4 rounded-full transition-all duration-500"
                        :style="{ width: completionPercentage + '%' }" />
                </div>
                <p class="text-sm mt-2 text-grey">{{ completionPercentage + '%' }} {{ $t('label.completed') }}</p>
            </div>
        </div>
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <!-- Display questions and responses -->
            <template v-for="(item, idx) in questionsResponses" :key="item.member_assessment_question_response_id || idx">
                <div class="col-span-2 mb-2 p-3 border rounded bg-gray-50">
                    <div class="font-semibold text-gray-700 mb-1">
                        {{ item.question_text }}
                    </div>
                    <div v-if="item.type.name == 'Short Answer'">
                        <StyledTextArea
                            v-model="questionsResponses[idx].responses[0]['short_answer']"
                            :disabled="!allowEdit"
                            :name="`question_${idx}`" />
                    </div>
                    <div v-if="item.type.name == 'Scaled'">
                        <StyledRadio
                            v-model="questionsResponses[idx].responses[0]['scaled_response_id']"
                            :options="scaledOptions"
                            :name="`question_${idx}`"
                            :disabled="!allowEdit" />
                    </div>
                </div>
            </template>
        </div>
    </form>
    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
